import {
  createAbortReason,
  getAbortReason,
  linkAbortSignals,
  type AbortReason,
} from './abort'

const MAX_DEADLINE_TIMEOUT_MS = 2_147_483_647

type DeadlineOptions = {
  readonly timeoutMs: number
  readonly signal?: AbortSignal
  readonly now?: () => number
}

type Deadline = {
  readonly signal: AbortSignal
  readonly startedAtMs: number
  readonly deadlineAtMs: number
  elapsedMs(): number
  reason(): AbortReason | undefined
  didExpire(): boolean
  dispose(): void
}

export function createDeadline(options: DeadlineOptions): Deadline {
  assertTimeout(options.timeoutMs)

  const now = options.now ?? Date.now
  const startedAtMs = now()
  const deadlineAtMs = startedAtMs + options.timeoutMs
  const deadlineController = new AbortController()
  const linkedController = linkAbortSignals([
    options.signal,
    deadlineController.signal,
  ])
  const deadlineReason = createAbortReason(
    'deadline-exceeded',
    `The operation exceeded its ${options.timeoutMs}ms deadline.`
  )
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined
  let disposed = false

  const clearTimer = (): void => {
    if (timer === undefined) {
      return
    }

    globalThis.clearTimeout(timer)
    timer = undefined
  }

  if (!linkedController.signal.aborted) {
    linkedController.signal.addEventListener('abort', clearTimer, {
      once: true,
    })

    if (options.timeoutMs === 0) {
      deadlineController.abort(deadlineReason)
    } else {
      timer = globalThis.setTimeout(() => {
        deadlineController.abort(deadlineReason)
      }, options.timeoutMs)
    }
  }

  return {
    signal: linkedController.signal,
    startedAtMs,
    deadlineAtMs,
    elapsedMs(): number {
      return Math.max(0, now() - startedAtMs)
    },
    reason(): AbortReason | undefined {
      return getAbortReason(linkedController.signal)
    },
    didExpire(): boolean {
      return (
        getAbortReason(linkedController.signal)?.code === 'deadline-exceeded'
      )
    },
    dispose(): void {
      if (disposed) {
        return
      }

      disposed = true

      clearTimer()

      if (!linkedController.signal.aborted) {
        linkedController.abort(
          createAbortReason('disposed', 'The deadline scope was disposed.')
        )
      }

      linkedController.dispose()
      linkedController.signal.removeEventListener('abort', clearTimer)
    },
  }
}

function assertTimeout(timeoutMs: number): void {
  if (
    !Number.isFinite(timeoutMs) ||
    timeoutMs < 0 ||
    timeoutMs > MAX_DEADLINE_TIMEOUT_MS
  ) {
    throw new RangeError(
      `timeoutMs must be between 0 and ${MAX_DEADLINE_TIMEOUT_MS}.`
    )
  }
}
